import { useEffect, useState } from 'react';
import { setTableSettingsRest } from '@/services/user/userSettingService';
import { Hotkeys, QuickActionButtons, TableSettings, TableSettingsFields } from '@/services/user/userSettingsTypes';
import { DeckDesign } from '@/assets/decks/DeckDesign';
import ToogleButton from '@/components/new-design/ToogleButton';
import { CardSet as CardsSimpleDeck } from '@/assets/decks/CardSimpleDeck';
import { CardSet as CardsSimpleDeckBlack } from '@/assets/decks/CardSimpleDeckBlack';
import { CardSet as CardsDoubleDeck } from '@/assets/decks/CardsDoubleDeck';
import { CardSet as CardsDoubleDeckBlack } from '@/assets/decks/CardsDoubleDeckBlack';
import { CardKey } from '@/assets/decks/CardKey';
import { useDispatch, useSelector } from 'react-redux';
import useGetTaleSettings from '@/services/user/queries/useGetTaleSettings';
import background01 from '@/assets/background01.png';
import background03 from '@/assets/background03.png';
import background04 from '@/assets/background04.png';
import background05 from '@/assets/background05.png';

import feltBlack from '@/assets/feltBlack.png';
import feltBlue from '@/assets/feltBlue.png';
import feltRed from '@/assets/feltRed.png';
import feltGreen from '@/assets/feltGreen.png';
import feltPurple from '@/assets/feltPurple.png';
import feltwhite from '@/assets/feltWhite.png';

import { authActionCreator, AuthEvents } from '@/services/auth/authActions';
import ButtonSettings from './ButtonSettings';
import HotkeysSection from './HotkeysSection';
import { isEqual } from 'lodash';
import useIsMobileView from '../../../../../hooks/useIsMobileView';
import useGetUser from '@/services/user/queries/useGetUser';
import { ReducerState } from '@/services/common/lib/reducerState';
import { DIMENSION_CLASSES } from '@/features/user-profile/profile-settings/constants';

const isQuickActionButtons = (value: any): value is QuickActionButtons => {
  return (
    value &&
    typeof value === 'object' &&
    Array.isArray(value.onBetButtons) &&
    Array.isArray(value.onPreFlopButtons) &&
    Array.isArray(value.postFlopButtons)
  );
};

const defaultBackgroundKey = 'default';

const feltColors: Record<string, string> = {
  purple: feltPurple,
  black: feltBlack,
  blue: feltBlue,
  red: feltRed,
  green: feltGreen,
  white: feltwhite,
};

const FeltColorCard: React.FC<{
  imageSrc: string;
  selected: boolean;
  setFeltColor: (field: TableSettingsFields, value: string) => void;
}> = ({ imageSrc, selected, setFeltColor }) => {
  return (
    <div
      className={`rounded-[20px] ${selected ? 'border-[3px] border-ph-purple' : 'rounded-5 m-0.5 bg-gradient-table-panel p-[1px]'}`}
    >
      <div
        className="h-20 w-20 cursor-pointer rounded-[20px] bg-ph-violet-dark"
        onClick={() => setFeltColor(TableSettingsFields.FeltColor, imageSrc)}
      >
        <img src={feltColors[imageSrc]} alt="background" className="h-20 w-20 rounded-[20px]" />
      </div>
    </div>
  );
};

const backgrounds: string[] = [defaultBackgroundKey, background01, background03, background04, background05]; // TODO : Implement background to let user select one of his choice

const BackgroundDesignCard: React.FC<{
  imageSrc: string;
  selected: boolean;
  setbackgroundDesign: (field: TableSettingsFields, value: string) => void;
}> = ({ imageSrc, selected, setbackgroundDesign }) => {
  return (
    <div
      className={`rounded-[20px] ${selected ? 'border-[3px] border-ph-purple' : 'rounded-5 m-0.5 bg-gradient-table-panel p-[1px]'}`}
    >
      <div
        className="h-20 w-20 cursor-pointer rounded-[20px] bg-ph-violet-dark"
        onClick={() => setbackgroundDesign(TableSettingsFields.BackgroundDesign, imageSrc)}
      >
        {imageSrc === defaultBackgroundKey ? null : (
          <img src={imageSrc} alt="background" className="h-20 w-20 rounded-[20px]" />
        )}
      </div>
    </div>
  );
};

const DeckDesignCard: React.FC<{
  deck: DeckDesign;
  selected: boolean;
  setDeckDesign: (type: TableSettingsFields, value: string) => void;
}> = ({ deck, selected, setDeckDesign }) => {
  const appliedDeck = (deck: DeckDesign) => {
    switch (deck) {
      case DeckDesign.simpleDeck: {
        return CardsSimpleDeck;
      }
      case DeckDesign.simpleDeckBlack: {
        return CardsSimpleDeckBlack;
      }
      case DeckDesign.doubleDeck: {
        return CardsDoubleDeck;
      }
      case DeckDesign.doubleDeckBlack: {
        return CardsDoubleDeckBlack;
      }
      default:
        return CardsDoubleDeck;
    }
  };

  return (
    <div
      className={`rounded-[20px] ${selected ? 'border-[3px] border-ph-purple' : 'rounded-5 m-0.5 bg-gradient-table-panel p-[1px]'}`}
    >
      <div
        className="flex h-[5em] w-[7.03125em] items-center justify-items-center gap-x-3 rounded-[1.25em] bg-ph-violet-dark"
        onClick={() => setDeckDesign(TableSettingsFields.DeckDesign, deck)}
      >
        <div className="mx-auto flex w-23 flex-row">
          <img src={appliedDeck(deck)[CardKey.sa]} alt={CardKey.sa} className="top-0 w-[1.875em] shadow-small-deck" />
          <img
            src={appliedDeck(deck)[CardKey.ca]}
            alt={CardKey.ca}
            className="relative right-[0.625em] top-0 w-[1.875em] shadow-small-deck"
          />
          <img
            src={appliedDeck(deck)[CardKey.da]}
            alt={CardKey.da}
            className="relative right-[1.25em] top-0 w-[1.875em] shadow-small-deck"
          />
          <img
            src={appliedDeck(deck)[CardKey.ha]}
            alt={CardKey.ha}
            className="relative right-[1.875em] top-0 w-[1.875em] shadow-small-deck"
          />
        </div>
      </div>
    </div>
  );
};

const TablePanel = () => {
  const userId = useSelector((state: ReducerState) => state.auth.id);
  const [tableSettingsState, setTableSettingsState] = useState<undefined | TableSettings>(undefined);
  const [buttonConfigurations, setButtonConfigurations] = useState<QuickActionButtons>();
  const [hotkeysConfiguration, setHotkeysConfiguration] = useState<Hotkeys>();
  const isMobileView = useIsMobileView({ checkForUserAgent: true });

  const { data } = useGetTaleSettings();
  const { data: userData } = useGetUser({
    userId: userId,
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (data) {
      setTableSettingsState(data.data);
      setButtonConfigurations({
        ...data.data.buttonConfigurations,
        postFlopButtons: data.data.buttonConfigurations.postFlopButtons.map((b) => {
          return { ...b, value: b.value * 100 };
        }),
      });
      setHotkeysConfiguration(data.data.hotkeys);
    }
  }, [data]);

  const setTableSettings = async (
    setting: TableSettingsFields,
    value: string | boolean | QuickActionButtons | Hotkeys,
  ) => {
    if (!tableSettingsState) return;

    if (setting === TableSettingsFields.ButtonConfigurations && isQuickActionButtons(value)) {
      value = {
        ...value,
        postFlopButtons: value.postFlopButtons.map((b) => {
          return { ...b, value: b.value / 100 };
        }),
      };
    }

    try {
      const response = await setTableSettingsRest({
        setting,
        value,
      });
      const newSettings = {
        ...tableSettingsState,
        ...response.data,
        ...{ [setting]: value },
      };

      setTableSettingsState(newSettings);

      dispatch(authActionCreator(AuthEvents.UPDATE_SETTINGS, newSettings));
    } catch (error) {
      console.log('error', error); // TODO
    }
  };

  useEffect(() => {
    if (buttonConfigurations && !isEqual(tableSettingsState?.buttonConfigurations, buttonConfigurations)) {
      setTableSettings(TableSettingsFields.ButtonConfigurations, buttonConfigurations);
    }

    if (hotkeysConfiguration && !isEqual(tableSettingsState?.hotkeys, hotkeysConfiguration)) {
      setTableSettings(TableSettingsFields.Hotkeys, hotkeysConfiguration);
    }
  }, [buttonConfigurations, hotkeysConfiguration]);

  return (
    <div className={DIMENSION_CLASSES} style={{ outline: 'none' }}>
      {tableSettingsState && (
        <>
          <div className="w-full pt-5">
            <p className="mb-5 text-xs font-semibold text-ph-gray-evening">Felt Colors</p>

            <div className="mb-10 flex flex-row gap-x-3 overflow-x-auto hide-scrollbar">
              {Object.keys(feltColors).map((key) => (
                <FeltColorCard
                  key={key}
                  imageSrc={key}
                  selected={tableSettingsState?.feltColor === key}
                  setFeltColor={setTableSettings}
                />
              ))}
            </div>
          </div>
          <div className="w-full pt-5">
            <p className="mb-5 text-xs font-semibold text-ph-gray-evening">Background</p>

            <div className="mb-10 flex flex-row gap-x-3 overflow-x-auto hide-scrollbar">
              {backgrounds.map((background) => (
                <BackgroundDesignCard
                  key={background}
                  imageSrc={background}
                  selected={tableSettingsState?.backgroundDesign === background}
                  setbackgroundDesign={setTableSettings}
                />
              ))}
            </div>
          </div>
          <div className="mb-6 w-full pt-5">
            <p className="mb-5 text-xs font-semibold text-ph-gray-evening">Cards Design</p>
            <div className="mb-10 flex flex-row gap-x-3 overflow-x-auto hide-scrollbar">
              {[DeckDesign.doubleDeck, DeckDesign.doubleDeckBlack].map((deckType) => (
                <DeckDesignCard
                  key={deckType} // Use the deck type as a key for React list rendering
                  deck={deckType}
                  selected={tableSettingsState.deckDesign === deckType}
                  setDeckDesign={setTableSettings}
                />
              ))}
            </div>
            <div className="flex flex-row gap-4">
              <div className="flex flex-col gap-6">
                <div>
                  <ToogleButton
                    enabled={tableSettingsState.autoTimeBankEnabled}
                    toogleAction={() => {
                      setTableSettings(
                        TableSettingsFields.AutoTimeBankEnabled,
                        !tableSettingsState.autoTimeBankEnabled,
                      );
                    }}
                    label={'Auto Enable Time Bank'}
                  />
                </div>
                <div>
                  <ToogleButton
                    enabled={tableSettingsState.stackInBigBlindsEnabled}
                    toogleAction={() => {
                      setTableSettings(
                        TableSettingsFields.StackInBigBlindsEnabled,
                        !tableSettingsState.stackInBigBlindsEnabled,
                      );
                    }}
                    label={'Display Stack in BB'}
                  />
                </div>
                <div>
                  <ToogleButton
                    enabled={!!tableSettingsState.fannedCardsDisplayEnabled}
                    toogleAction={() => {
                      setTableSettings(
                        TableSettingsFields.FannedCardsDisplayEnabled,
                        !tableSettingsState.fannedCardsDisplayEnabled,
                      );
                    }}
                    label={'Display Fanned Cards'}
                  />
                </div>
                {!userData?.data?.isAmbassador && (
                  <div>
                    <ToogleButton
                      enabled={!!tableSettingsState.showRealNameEnabled}
                      toogleAction={() => {
                        setTableSettings(
                          TableSettingsFields.ShowRealNameEnabled,
                          !tableSettingsState.showRealNameEnabled,
                        );
                      }}
                      label={'Display Full Name'}
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-6">
                <div>
                  <ToogleButton
                    enabled={tableSettingsState.displayFoldedCardsEnabled}
                    toogleAction={() => {
                      setTableSettings(
                        TableSettingsFields.DisplayFoldedCardsEnabled,
                        !tableSettingsState?.displayFoldedCardsEnabled,
                      );
                    }}
                    label={'Display Folded Cards'}
                  />
                </div>
                <ToogleButton
                  enabled={tableSettingsState.soundEnabled}
                  toogleAction={() => {
                    setTableSettings(TableSettingsFields.SoundEnabled, !tableSettingsState.soundEnabled);
                  }}
                  label={'Enable Sounds'}
                />

                <ToogleButton
                  enabled={tableSettingsState.modelsEnabled}
                  toogleAction={() => {
                    setTableSettings(TableSettingsFields.ModelsEnabled, !tableSettingsState.modelsEnabled);
                  }}
                  label={'Enable 3D Models'}
                />

                {/* <ToogleButton
                  enabled={tableSettingsState.tableTabsInDesktopEnabled}
                  toogleAction={() => {
                    setTableSettings(
                      TableSettingsFields.TableTabsInDesktopEnabled,
                      !tableSettingsState.tableTabsInDesktopEnabled,
                    );
                  }}
                  label={'Enable Table Tabs on Desktop'}
                /> */}
              </div>
            </div>
            <div></div>
          </div>

          {buttonConfigurations && (
            <ButtonSettings
              buttonConfigurations={buttonConfigurations}
              setButtonConfigurations={setButtonConfigurations}
            />
          )}

          {hotkeysConfiguration && !isMobileView && (
            <div className="mt-9 flex items-center justify-center">
              <HotkeysSection hotkeys={hotkeysConfiguration} setHotkeysConfiguration={setHotkeysConfiguration} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TablePanel;
